import urls from "global/constants/UrlConstants";
import { getCallParams, makeCall } from "utils/service";

/**
 * Share a single sign request with another account user (VIEW grant). The backend fills ownerEmail
 * from the caller; we only supply who to share with and (optionally) when the grant expires.
 * POST sign/add/share/request  ->  returns the grant id.
 */
export const shareRequest = async (
  requestId: string,
  granteeEmail: string,
  expiresOn?: string | null,
): Promise<any> => {
  const body: any = { requestId, granteeEmail };
  if (expiresOn) body.expiresOn = expiresOn;
  const callParams = await getCallParams("POST", body);
  return await makeCall(urls.SHARE_REQUEST, callParams);
};

/** Active share grants on a request (who it's shared with). GET sign/fetch/share/request/{requestId} */
export const fetchSharesForRequest = async (
  requestId: string,
): Promise<any[]> => {
  const callParams = await getCallParams("GET");
  const response: any = await makeCall(
    `${urls.SHARES_FOR_REQUEST}/${requestId}`,
    callParams,
  );
  return Array.isArray(response) ? response : [];
};

/** Revoke a previously granted share. DELETE sign/update/revoke/share/{grantId} (sign:update tier). */
export const revokeShare = async (grantId: string): Promise<any> => {
  const callParams = await getCallParams("DELETE");
  return await makeCall(`${urls.REVOKE_SHARE}/${grantId}`, callParams);
};

/** Requests shared with the current user. POST sign/fetch/shared/requests/status/{status}/page/{page} */
export const fetchSharedRequests = async (
  status: string = "All",
  page: number = 0,
  searchText: string = "",
): Promise<any[]> => {
  const url = `${urls.SHARED_REQUESTS}/${status}/page/${page}`;
  const callParams = await getCallParams("POST", { searchText, entitySorters: [] });
  const response: any = await makeCall(url, callParams);
  return Array.isArray(response) ? response : [];
};

/**
 * Admin-only: reassign a departing user's sign requests to a new owner (offboarding custody transfer).
 * Pass fromOwnerEmail to move ALL of that user's requests. POST sign/update/transfer/ownership -> count moved.
 */
export const transferOwnership = async (
  fromOwnerEmail: string,
  newOwnerEmail: string,
): Promise<number> => {
  const callParams = await getCallParams("POST", { fromOwnerEmail, newOwnerEmail });
  const response: any = await makeCall(urls.TRANSFER_OWNERSHIP, callParams);
  return typeof response === "number" ? response : (response?.count ?? 0);
};
